import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { Transition } from '@uirouter/angular';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import {
    ChecklistV2Edit_isChatAvailable,
    ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    setChecklistDataToStore,
} from './checklist-v2.states';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { isDefined } from '@uirouter/core';
import { IChecklistV2 } from '@fi-shared/checklist/checklist-v2';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { cloneDeep } from '@fi-shared/utils';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';
import { FiContentLanguageSharedService } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared.service';

export const ChecklistV2Add_checklistFn = [
    FiStateService,
    Transition,
    FiChecklistHelperService,
    FiChecklistEditorStoreService,
    FiCheckpoint2HelperService,
    (
        fiStateService: FiStateService,
        tr: Transition,
        fiChecklistHelperService: FiChecklistHelperService,
        fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    ) => {
        const checklist = fiStateService.getParams(tr).checklist ?? fiChecklistHelperService.getChecklistInstanceV2();
        fiChecklistEditorStoreService.setInitialChecklistData(fiChecklistHelperService.getChecklistInstanceV2());
        setChecklistDataToStore(checklist, fiCheckpoint2HelperService, fiChecklistEditorStoreService, true);
    },
];

export const ChecklistV2Add_templateFn = [
    FiStateService,
    Transition,
    FiChecklistHelperService,
    FiChecklistEditorStoreService,
    FiCheckpoint2HelperService,
    (
        fiStateService: FiStateService,
        tr: Transition,
        fiChecklistHelperService: FiChecklistHelperService,
        fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    ) => {
        const checkListTemplate =
            fiStateService.getParams(tr).checklistTemplate ?? fiChecklistHelperService.getChecklistInstanceV2();
        fiChecklistEditorStoreService.setInitialChecklistData(fiChecklistHelperService.getChecklistInstanceV2());
        setChecklistDataToStore(checkListTemplate, fiCheckpoint2HelperService, fiChecklistEditorStoreService, true);
    },
];

export const ChecklistV2EditCheckpointSectionsState_handleChecklistFn = [
    FiStateService,
    Transition,
    FiChecklistEditorStoreService,
    FiChecklist2Service,
    FiCheckpoint2HelperService,
    FiChecklistHelperService,
    (
        stateService: FiStateService,
        transition: Transition,
        fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        fiChecklist2Service: FiChecklist2Service,
        fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        fiChecklistHelperService: FiChecklistHelperService,
    ) => {
        const checklistId: number = coerceNumberProperty(stateService.getParams(transition).id);
        return new Promise<void>((resolve, reject) => {
            if (!isDefined(fiChecklistEditorStoreService.getInitialChecklistData())) {
                if (!isDefined(checklistId)) {
                    setChecklistDataToStore(
                        fiChecklistHelperService.getChecklistInstanceV2(),
                        fiCheckpoint2HelperService,
                        fiChecklistEditorStoreService,
                    );
                    resolve();
                }
                fiChecklist2Service
                    .getChecklist(checklistId, stateService.getParams(transition).isAudit)
                    .then((checklistData: IChecklistV2) => {
                        setChecklistDataToStore(
                            checklistData,
                            fiCheckpoint2HelperService,
                            fiChecklistEditorStoreService,
                        );
                        resolve();
                    })
                    .catch(reject);
            } else {
                resolve();
            }
        });
    },
];

export const ChecklistV2_EditCheckpointSectionsResolve = {
    checklistId: [
        FiStateService,
        Transition,
        (stateService: FiStateService, transition: Transition) => {
            return coerceNumberProperty(stateService.getParams(transition).id);
        },
    ],
    accessRightFilterCollection: [
        FiStateService,
        Transition,
        AccessRightService,
        ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    ],
    isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    handleChecklist: ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
};

export const ChecklistV2_EditCheckpointResolve = {
    id: [
        FiStateService,
        Transition,
        (stateService: FiStateService, transition: Transition) => {
            return coerceNumberProperty(stateService.getParams(transition).id);
        },
    ],
    checkpointGroupIndex: [
        FiStateService,
        Transition,
        (stateService: FiStateService, transition: Transition) => {
            return coerceNumberProperty(stateService.getParams(transition).checkpointGroupIndex);
        },
    ],
    accessRightFilterCollection: [
        FiStateService,
        Transition,
        AccessRightService,
        ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    ],
    isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    checkpointIndex: [
        FiStateService,
        Transition,
        (stateService: FiStateService, transition: Transition) => {
            return coerceNumberProperty(stateService.getParams(transition).checkpointIndex);
        },
    ],
    checkpoint: [
        FiStateService,
        Transition,
        FiChecklistEditorStoreService,
        (
            stateService: FiStateService,
            transition: Transition,
            fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        ) => {
            const currentCheckpointGroups = fiChecklistEditorStoreService.getCurrentCheckpointGroups();
            const checkpointGroupIndex = coerceNumberProperty(stateService.getParams(transition).checkpointGroupIndex);
            const checkpointIndex = coerceNumberProperty(stateService.getParams(transition).checkpointIndex);
            if (isDefined(currentCheckpointGroups))
                return cloneDeep(currentCheckpointGroups[checkpointGroupIndex].Checkpoints[checkpointIndex]);

            return undefined;
        },
    ],
    checklistName: [
        FiChecklistEditorStoreService,
        FiContentLanguageSharedService,
        (
            fiChecklistEditorStoreService: FiChecklistEditorStoreService,
            fiContentLanguageSharedService: FiContentLanguageSharedService,
        ) => {
            const currentChecklist = fiChecklistEditorStoreService.getCurrentChecklistData$().value;
            if (isDefined(currentChecklist)) {
                return fiContentLanguageSharedService.getContentLanguageValueForDisplayContent(currentChecklist.Names);
            }
            return undefined;
        },
    ],
    handleChecklist: ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
};

export const ChecklistV2TemplateEdit_getTemplateFn = [
    FiStateService,
    Transition,
    FiChecklist2TemplateService,
    FiCheckpoint2HelperService,
    FiChecklistEditorStoreService,
    (
        fiStateService: FiStateService,
        tr: Transition,
        fiChecklist2TemplateService: FiChecklist2TemplateService,
        fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        fiChecklistEditorStoreService: FiChecklistEditorStoreService,
    ) => {
        const checklistTemplateId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return new Promise<void>((resolve, reject) => {
            fiChecklist2TemplateService
                .getChecklistTemplate(checklistTemplateId, fiStateService.getParams(tr).isAudit)
                .then((checklistData: IChecklistV2) => {
                    setChecklistDataToStore(checklistData, fiCheckpoint2HelperService, fiChecklistEditorStoreService);
                    resolve();
                })
                .catch(reject);
        });
    },
];

export const ChecklistV2Add_templateCopyChecklistFn = [
    FiStateService,
    Transition,
    FiChecklist2TemplateService,
    FiCheckpoint2HelperService,
    FiChecklistEditorStoreService,
    FiChecklistHelperService,
    (
        stateService: FiStateService,
        transition: Transition,
        fiChecklist2TemplateService: FiChecklist2TemplateService,
        fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        fiChecklistEditorStoreService: FiChecklistEditorStoreService,
        fiChecklistHelperService: FiChecklistHelperService,
    ) => {
        const checklistTemplateId = coerceNumberProperty(stateService.getParams(transition).id);
        const currentChecklist: IChecklistV2 = fiChecklistEditorStoreService.getCurrentChecklistData$().value;

        return new Promise<void>((resolve, reject) => {
            if (isDefined(currentChecklist)) {
                resolve();
            } else {
                fiChecklist2TemplateService
                    .getChecklistFromTemplate(checklistTemplateId, stateService.getParams(transition).isAudit)
                    .then((checklist) => {
                        const newChecklist = fiChecklistHelperService.getChecklistInstanceV2();
                        newChecklist.Names = fiCheckpoint2HelperService.getContentLanguageLabelModels(
                            newChecklist.Names,
                        );
                        newChecklist.Descriptions = fiCheckpoint2HelperService.getContentLanguageLabelModels(
                            newChecklist.Descriptions,
                        );
                        fiChecklistEditorStoreService.setInitialChecklistData(newChecklist);
                        checklist.Names = fiCheckpoint2HelperService.getContentLanguageLabelModels(checklist.Names);
                        checklist.Descriptions = fiCheckpoint2HelperService.getContentLanguageLabelModels(
                            checklist.Descriptions,
                        );
                        checklist.IsShared = true;
                        fiChecklistEditorStoreService.setCurrentChecklistData$(cloneDeep(checklist));
                        resolve();
                    })
                    .catch(reject);
            }
        });
    },
];

export const ChecklistV2Template_AddCheckpointSectionsResolve = {
    accessRightFilterCollection: [
        FiStateService,
        Transition,
        AccessRightService,
        ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    ],
    isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    handleChecklist: ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
};

export const ChecklistV2_templateCopyAddCheckpointSectionsResolve = {
    accessRightFilterCollection: [
        FiStateService,
        Transition,
        AccessRightService,
        ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    ],
    isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    checklist: ChecklistV2Add_templateCopyChecklistFn,
};

export const onChecklistEditorStateExit = (transition: Transition) => {
    const injector = transition.injector();
    const fiChecklistEditorStoreService: FiChecklistEditorStoreService = injector.get(FiChecklistEditorStoreService);
    fiChecklistEditorStoreService.resetChecklistEditorStoreData();
};
